import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Send, Copy, Link, Users, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const InvitePage = () => {
  const { user } = useOutletContext();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const publicLink = `${window.location.origin}/onboard`;

  useEffect(() => { loadInvites(); }, []);

  const loadInvites = async () => {
    try {
      const res = await api.get('/invite/list');
      setInvites(res.data);
    } catch (e) {
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!email.trim()) { toast.error('Enter an email'); return; }
    setSending(true);
    try {
      await api.post('/invite/send', { email: email.trim() });
      toast.success(`Invite sent to ${email}`);
      setEmail('');
      loadInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const copyLink = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied!');
  };

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="invite-title">Invite Translators</h1>
          <p className="text-muted-foreground">Send onboarding invites or share the public link</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Send Invite */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4" /> Send Email Invite</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">The translator will receive an email with a unique onboarding link.</p>
              <div className="flex gap-2">
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="translator@email.com"
                  data-testid="invite-email-input"
                />
                <Button onClick={handleSend} disabled={sending} data-testid="send-invite-btn">
                  <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Public Link */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Link className="w-4 h-4" /> Public Onboarding Link</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">Anyone with this link can self-onboard as a translator.</p>
              <div className="flex gap-2">
                <Input value={publicLink} readOnly className="font-mono text-xs" />
                <Button variant="outline" onClick={() => copyLink(publicLink)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invite History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Invite History ({invites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {invites.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No invites sent yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center">#</TableHead>
                      <TableHead className="text-center">Email</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Sent At</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invites.map((inv, i) => (
                      <TableRow key={inv.invite_id}>
                        <TableCell className="text-center text-xs text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="text-center text-sm">{inv.email}</TableCell>
                        <TableCell className="text-center">
                          {inv.status === 'used'
                            ? <Badge className="bg-green-100 text-green-700 text-xs">Onboarded</Badge>
                            : <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>}
                        </TableCell>
                        <TableCell className="text-center text-xs text-muted-foreground">{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="ghost" size="sm" className="h-7" onClick={() => copyLink(`${window.location.origin}/onboard?code=${inv.invite_code}`)}>
                              <Copy className="w-3 h-3 mr-1" /> Copy
                            </Button>
                            {inv.status !== 'used' && (
                              <Button variant="ghost" size="sm" className="h-7" onClick={async () => {
                                try { await api.post(`/invite/${inv.invite_id}/resend`); toast.success('Invite resent!'); } catch (e) { toast.error(e.response?.data?.detail || 'Resend failed'); }
                              }}>
                                <RefreshCw className="w-3 h-3 mr-1" /> Resend
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-7 text-destructive" onClick={async () => {
                              if (!confirm('Delete this invite?')) return;
                              try { await api.delete(`/invite/${inv.invite_id}`); toast.success('Deleted'); loadInvites(); } catch (e) { toast.error('Failed'); }
                            }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default InvitePage;
